import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { CategoryFormDialog } from '@/features/inventory/CategoryFormDialog'
import { deleteCategory, listCategories, type Category } from '@/features/inventory/api'
import { toast } from '@/lib/toast'

interface Row {
  category: Category
  depth: number
}

/** Depth-first walk of the tree into the flat, indented rows the table renders. */
function flatten(nodes: Category[], depth = 0): Row[] {
  return nodes.flatMap((category) => [
    { category, depth },
    ...flatten(category.children ?? [], depth + 1),
  ])
}

/**
 * The category tree, editable. Products and tax rules both point at a category by id, so
 * this is catalog structure -- gated the same way as `TaxRules`, on the `products`
 * permission cell rather than a resource of its own. See `catalog.router`'s module docstring.
 */
export function Categories() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Category | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Category | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    retry: false,
  })

  const canWrite = can('products', 'create')
  const canDelete = can('products', 'delete')

  const remove = useMutation({
    mutationFn: (category: Category) => deleteCategory(category.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['categories'] })
      toast.success('Category deleted.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.')
      setConfirmDelete(null)
    },
  })

  const tree = data ?? []
  const rows = flatten(tree)

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Name',
      className: 'font-medium',
      cell: ({ category, depth }) => (
        <span style={{ paddingLeft: `${depth * 1.25}rem` }}>{category.name}</span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      cell: ({ category }) =>
        category.description ?? <span className="text-slate-400 dark:text-slate-500">—</span>,
    },
    {
      key: 'sort_order',
      header: 'Sort order',
      className: 'text-right tabular-nums',
      headerClassName: 'text-right',
      cell: ({ category }) => category.sort_order,
    },
    ...(canWrite || canDelete
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: ({ category }: Row) => (
              <div className="flex justify-end gap-2">
                {canWrite && (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(category)}>
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(category)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          } satisfies Column<Row>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {rows.length} categor{rows.length === 1 ? 'y' : 'ies'} — how products and tax
            rules are grouped.
          </p>
        </div>
        {canWrite && <Button onClick={() => setDialogOpen(true)}>Add category</Button>}
      </header>

      <Card>
        <CardHeader
          title="Category tree"
          description="A category can only be deleted once its subcategories and products have moved elsewhere."
        />
        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(row) => row.category.id}
          caption="Categories"
          isLoading={isLoading}
          error={error}
          emptyMessage="No categories yet."
        />
      </Card>

      {dialogOpen && (
        <CategoryFormDialog open tree={tree} onClose={() => setDialogOpen(false)} />
      )}

      {editing && (
        <CategoryFormDialog
          open
          category={editing}
          tree={tree}
          onClose={() => setEditing(null)}
        />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : 'Delete category'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          Refused if this category still has subcategories or products in it — move or
          reassign them first.
        </Alert>
      </Modal>
    </div>
  )
}
